import React, { useMemo } from "react";
import styles from "./style.module.scss";

function normalizeSparklineValues(data) {
  const values = (data || []).map((v) => Number(v)).filter((v) => Number.isFinite(v));
  if (!values.length) return [];
  const min = Math.min(...values);
  const max = Math.max(...values);
  if (min === max) {
    return values.map(() => 0.5);
  }
  return values.map((v) => (v - min) / (max - min));
}

function buildSparklineCoordinates(normalized, width, height) {
  const padX = 2;
  const padY = 4;
  const innerW = Math.max(1, width - padX * 2);
  const innerH = Math.max(1, height - padY * 2);
  return normalized.map((value, index) => {
    const x =
      normalized.length === 1
        ? padX + innerW / 2
        : padX + (index / (normalized.length - 1)) * innerW;
    const y = padY + (1 - value) * innerH;
    return { x, y };
  });
}

/** Catmull-Rom spline converted to cubic Bezier path segments. */
export function buildSmoothSparklinePath(coordinates) {
  if (!coordinates?.length) return "";
  if (coordinates.length === 1) {
    const { x, y } = coordinates[0];
    return `M ${x.toFixed(2)} ${y.toFixed(2)}`;
  }
  if (coordinates.length === 2) {
    const [a, b] = coordinates;
    return `M ${a.x.toFixed(2)} ${a.y.toFixed(2)} L ${b.x.toFixed(2)} ${b.y.toFixed(2)}`;
  }

  let path = `M ${coordinates[0].x.toFixed(2)} ${coordinates[0].y.toFixed(2)}`;
  for (let index = 0; index < coordinates.length - 1; index += 1) {
    const p0 = coordinates[index === 0 ? 0 : index - 1];
    const p1 = coordinates[index];
    const p2 = coordinates[index + 1];
    const p3 = coordinates[index + 2] || p2;
    const cp1x = p1.x + (p2.x - p0.x) / 6;
    const cp1y = p1.y + (p2.y - p0.y) / 6;
    const cp2x = p2.x - (p3.x - p1.x) / 6;
    const cp2y = p2.y - (p3.y - p1.y) / 6;
    path += ` C ${cp1x.toFixed(2)} ${cp1y.toFixed(2)}, ${cp2x.toFixed(2)} ${cp2y.toFixed(2)}, ${p2.x.toFixed(2)} ${p2.y.toFixed(2)}`;
  }
  return path;
}

/**
 * Inline SVG sparkline for dashboard review trends.
 * Line color stays neutral; direction coloring is handled by the parent badge.
 */
export default function TrendSparkline({
  data = [],
  width = 140,
  height = 40,
  className,
}) {
  const path = useMemo(() => {
    const normalized = normalizeSparklineValues(data);
    if (!normalized.length) return "";
    const coordinates = buildSparklineCoordinates(normalized, width, height);
    return buildSmoothSparklinePath(coordinates);
  }, [data, width, height]);

  if (!path) {
    return (
      <svg
        className={`${styles.sparkline} ${className || ""}`.trim()}
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        aria-hidden
      />
    );
  }

  return (
    <svg
      className={`${styles.sparkline} ${className || ""}`.trim()}
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      aria-hidden
    >
      <path d={path} className={styles.line} />
    </svg>
  );
}

export function TrendChangeBadge({ trend, t, className }) {
  if (!trend) return null;
  if (trend.is_new) {
    return (
      <span className={`${styles.badgeNew} ${className || ""}`.trim()}>
        {t("all_dashboards_page.trendNew")}
      </span>
    );
  }
  const pct = Number(trend.change_pct);
  if (!Number.isFinite(pct)) return null;
  if (pct === 0) {
    return (
      <span className={`${styles.badgeFlat} ${className || ""}`.trim()}>
        0%
      </span>
    );
  }
  const positive = pct > 0;
  return (
    <span
      className={`${positive ? styles.badgeUp : styles.badgeDown} ${
        className || ""
      }`.trim()}
    >
      {positive ? "↑" : "↓"} {Math.abs(pct)}%
    </span>
  );
}

export function DashboardTrendSection({
  trend,
  loading = false,
  variant = "grid",
  t,
  className,
}) {
  const width = variant === "list" ? 100 : 140;
  const height = variant === "list" ? 24 : 40;

  if (loading || !trend?.daily?.length) {
    return (
      <div
        className={`${styles.placeholder} ${className || ""}`.trim()}
        style={{ width, height }}
        aria-hidden
      />
    );
  }

  return (
    <div className={`${styles.section} ${className || ""}`.trim()}>
      <TrendSparkline data={trend.daily} width={width} height={height} />
      <TrendChangeBadge trend={trend} t={t} className={styles.badge} />
    </div>
  );
}
